import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Eye, HeartHandshake, ArrowUpRight } from "lucide-react";
import { STATS } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const VALUES = [
  { icon: Target, title: "Mission", text: "Empower every small and medium business in India with the same digital firepower the big brands enjoy — at a price that makes sense." },
  { icon: Eye, title: "Vision", text: "A digital-first India where great local businesses are discovered, trusted and chosen online, every single day." },
  { icon: HeartHandshake, title: "Values", text: "Honesty in reporting, obsession with results, and partnerships that outlast a single campaign." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-16">
      <section className="bg-[#FDFBF7] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-4">About Levaze</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1014]">
              A growth partner for <span className="italic text-[#6C0B1B]">ambitious</span> businesses.
            </h1>
            <p className="mt-6 text-lg text-[#5C4B51] leading-relaxed">
              Levaze began with a simple observation: brilliant small businesses were losing customers online simply because their digital presence didn't match the quality of their work. We exist to fix that.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#F3EFE9] py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.img {...fadeUp}
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwYWdlbmN5JTIwbWFya2V0aW5nJTIwdGVhbXxlbnwwfHx8fDE3ODc5MTI3OTF8MA&ixlib=rb-4.1.0&q=85"
            alt="Levaze workspace" className="rounded-2xl w-full h-[400px] object-cover shadow-[0_30px_60px_-20px_rgba(108,11,27,0.25)]" />
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1014]">Our story</h2>
            <p className="mt-5 text-lg text-[#5C4B51] leading-relaxed">
              What started as a two-person web design studio in Mumbai is now a full-service digital agency with offices in Noida, Mira Road and Haridwar. Over 120 projects later, our formula hasn't changed: listen first, strategise second, execute relentlessly.
            </p>
            <p className="mt-4 text-lg text-[#5C4B51] leading-relaxed">
              From a boutique's first website to a manufacturer's national ad campaign — we treat every rupee of your budget like it's our own.
            </p>
            <div className="grid grid-cols-2 gap-6 mt-9">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`about-stat-${s.label}`}>
                  <p className="font-heading text-4xl font-light text-[#6C0B1B]">{s.value}</p>
                  <p className="text-sm text-[#5C4B51] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#FDFBF7] py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} data-testid={`about-value-${v.title.toLowerCase()}`}
                className="border border-[#6C0B1B]/10 rounded-2xl p-8 bg-white shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
                <div className="w-12 h-12 rounded-xl bg-[#6C0B1B]/8 flex items-center justify-center text-[#6C0B1B]">
                  <v.icon size={22} />
                </div>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-[#1A1014]">{v.title}</h3>
                <p className="mt-3 text-[15px] text-[#5C4B51] leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-16 rounded-2xl bg-[#6C0B1B] text-white p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light">Want to meet the people behind the work?</h2>
              <p className="mt-2 text-white/70">Say hello to the Levaze team.</p>
            </div>
            <Link to="/team" data-testid="about-team-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#6C0B1B] px-7 py-3.5 font-medium hover:bg-[#F3EFE9] transition-colors duration-200 shrink-0">
              Meet the Team <ArrowUpRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
